<template>
  <div v-if="description.length > 0">
    <i18n-t
      tag="p"
      class="text tw-mt-2 tw-overflow-hidden tw-font-normal"
      :keypath="i18nDescriptionKey"
      :class="{ 'is-expanded': isExpanded }"
    >
      <template
        v-for="[key, name, slug] in i18nDescriptionParams"
        #[key]
        :key="name"
      >
        <NuxtLinkLocale
          :to="`/influencer/profile/${slug}/`"
          class="tw-underline"
          target="_blank"
        >
          {{ name }}
        </NuxtLinkLocale>
      </template>
    </i18n-t>
    <button
      v-if="isLongDescription || isMobile"
      class="tw-mt-1 tw-border-none tw-bg-transparent tw-p-0 tw-font-medium tw-text-gray-500 tw-underline"
      type="button"
      @click="handleClick"
    >
      {{ ctaTitle }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { camelCase } from '~/mixins/global'

import { useSeoListGeneration } from '~/composables/useSeoListGeneration'
import { useSeoListUrlDataToEntity } from '~/composables/useSeoListUrlDataToEntity'

import { useMiscResizeStore } from '~/stores/miscResize'

import { Breakpoints } from '~/enums/breakpoints'

import type Tag from '~/entities/tag'
import type TagParent from '~/entities/tagParent'
import type { Influencer, StatsV3Influencer } from '~/types/influencer'
import type { SeoListsAsyncData } from '~/types/influencerListTagSeo'

type Props = {
  influencers: (Influencer | StatsV3Influencer)[]
  urlData: SeoListsAsyncData['urlData']
}

const props = defineProps<Props>()

const { entities } = useSeoListUrlDataToEntity(props.urlData)
const { genEntitiesFromUrlData } = useSeoListGeneration()
const { t, te } = useI18n()

const DESCRIPTION_CHAR_MAX = 500 as const
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const i18nDescriptionKey = ref('')
const i18nDescriptionParams = ref<string[][]>([])
const isExpanded = ref(false)

const isLongDescription = computed<boolean>(
  () => description.value.length > DESCRIPTION_CHAR_MAX,
)
const isMobile = computed<boolean>(() => SCREEN_WIDTH.value < Breakpoints.sm)
const ctaTitle = computed(() =>
  isExpanded.value
    ? t('components.seo.description.cta.showLess')
    : t('components.seo.description.cta.showMore'),
)
const description = computed(() =>
  generateDescriptionFromUrlData(
    props.urlData,
    props.influencers,
    entities.value,
  ),
)

function handleClick() {
  isExpanded.value = !isExpanded.value
}

/**
 * Create a i18n key from tags.
 *
 * @param  tags - An array of tags.
 * @returns  - A string of tags, separated by a dot.
 * @example
 * createI18nDescriptionKeyFromTags(tags) // returns "influencer.list.tags.tagKey"
 */
function createI18nDescriptionKeyFromTags(tags: (Tag | TagParent)[]): string {
  const tagKey =
    tags.map((tag) => camelCase(tag.name)).join('.') +
    (tags.length === 1 ? '._solo_' : '')

  return `influencer.list.tags.${tagKey}`
}

/**
 * Transform an array of influencers into an object.
 *
 * @param  influencers - An array of influencers.
 * @returns  - An object of i18n params.
 * @example formatI18nParamsFromInfluencers(influencers) // returns [ "influencerName0": "inf name", ...n ]
 */
function formatI18nParamsFromInfluencers(
  influencers: (Influencer | StatsV3Influencer)[],
): string[][] {
  return influencers
    .slice(0, 3)
    .map(({ entity, slug }, i) => [`influencerName${i}`, entity, slug])
}

/**
 * Get description from url data and return the related description if found.
 *
 * @param  urlData - The current url data.
 * @param  influencers - An array of influencers.
 * @param  entities - An array of tags.
 * @returns - A translated string.
 * @example getDescriptionFromUrlData(urlData, influencers, entities) // returns "lorem ipsum [...]"
 */
function generateDescriptionFromUrlData(
  urlData: SeoListsAsyncData['urlData'],
  influencers: (Influencer | StatsV3Influencer)[],
  entities?: (Tag | TagParent)[],
) {
  // make sure you have entities before you start processing
  if (!entities) entities = genEntitiesFromUrlData(urlData)

  i18nDescriptionKey.value = createI18nDescriptionKeyFromTags(entities)
  i18nDescriptionParams.value = formatI18nParamsFromInfluencers(influencers)

  // then "boom shaka laka!", we can display the formatted description (brr brrr)
  return te(i18nDescriptionKey.value) ? t(i18nDescriptionKey.value) : ''
}
</script>

<style lang="scss" scoped>
.text {
  display: -webkit-box;
  text-overflow: ellipsis;
  word-wrap: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;

  &.is-expanded {
    text-overflow: normal;
    -webkit-line-clamp: unset;
    line-clamp: unset;
  }
}
</style>
